import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  ChartBarIcon, UsersIcon, TicketIcon, ArrowsRightLeftIcon,
  StarIcon, DocumentTextIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon,
  FunnelIcon, GiftIcon, BellIcon, ClockIcon, ChatBubbleLeftRightIcon,
  MegaphoneIcon, BuildingStorefrontIcon, QrCodeIcon, FlagIcon, ArrowPathIcon,
  ExclamationTriangleIcon, ArrowUturnLeftIcon, BanknotesIcon, UserGroupIcon, ShieldExclamationIcon, KeyIcon, BellAlertIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon as PinSolid } from '@heroicons/react/24/solid';
import { MapPinIcon as PinOutline } from '@heroicons/react/24/outline';
import logoImg from '../assets/logo.png';
import iconImg from '../assets/icon.png';
import { adminAPI } from '../services/api';
import styles from './Layout.module.css';

const NAV_ITEMS = [
  // Banknotes rather than a storefront: All Customers already uses the
  // storefront, and two identical icons in one sidebar is a misclick.
  { path: '/counter',   label: 'Counter',    Icon: BanknotesIcon, store: true , perm: 'counter.use' },
  { path: '/dashboard', label: 'Dashboard',  Icon: ChartBarIcon , perm: 'dashboard.view' },
  { path: '/activity',  label: 'Activity',   Icon: ClockIcon , perm: 'activity.view' },
  { path: '/abandoned', label: 'Unfinished', Icon: ExclamationTriangleIcon , perm: 'payments.view' },
  { path: '/refunds',   label: 'Refunds',    Icon: ArrowUturnLeftIcon , perm: 'refunds.view' },
  { path: '/members',   label: 'App Members', Icon: UsersIcon , perm: 'members.view' },
  { path: '/bc-members', label: 'All Customers', Icon: BuildingStorefrontIcon , perm: 'bcmembers.view' },
  { path: '/qr-check',   label: 'Check a QR',    Icon: QrCodeIcon , perm: 'qrcheck.use' },
  { path: '/reports',    label: 'Reports',       Icon: FlagIcon , perm: 'reports.view' },
  { path: '/sync-health', label: 'Sync Health',  Icon: ArrowPathIcon , perm: 'synchealth.view' },
  { path: '/coupons',   label: 'Coupons',    Icon: TicketIcon , perm: 'coupons.manage' },
  { path: '/referrals', label: 'Referrals',  Icon: ArrowsRightLeftIcon , perm: 'referrals.view' },
  { path: '/loyalty',   label: 'Loyalty',    Icon: StarIcon , perm: 'loyalty.manage' },
  { path: '/crm',            label: 'CRM Segments', Icon: FunnelIcon , perm: 'crm.view' },
  { path: '/promo-codes',    label: 'Promo Codes',  Icon: GiftIcon , perm: 'promocodes.manage' },
  { path: '/notifications',  label: 'Notifications', Icon: BellIcon , perm: 'notifications.send' },
  { path: '/campaigns',      label: 'WhatsApp',     Icon: ChatBubbleLeftRightIcon , perm: 'campaigns.view' },
  { path: '/promotions', label: 'Promotions', Icon: MegaphoneIcon , perm: 'promotions.manage' },
  { path: '/pages',     label: 'App Pages',  Icon: DocumentTextIcon , perm: 'pages.manage' },
  { path: '/counter-staff', label: 'Counter Staff', Icon: UserGroupIcon , perm: 'staff.manage' },
  { path: '/scan-alerts', label: 'Scan Alerts', Icon: ShieldExclamationIcon , perm: 'scanalerts.view' },
  { path: '/users',     label: 'Dashboard Users', Icon: KeyIcon, perm: 'users.manage' },
  { path: '/settings',  label: 'Settings',   Icon: Cog6ToothIcon , perm: 'settings.manage' },
];

const PAGE_TITLES = {
  '/counter-staff': 'Counter Staff',
  '/scan-alerts': 'Scan Alerts',
  '/users': 'Dashboard Users',
  '/users': 'Dashboard Users',
  '/counter':   'Counter',
  '/dashboard': 'Dashboard',
  '/activity':  'Activity',
  '/abandoned': 'Unfinished checkouts',
  '/refunds':   'Refunds',
  '/members':   'App Members',
  '/bc-members': 'All Customers (Business Central)',
  '/qr-check':   'Check a VIP QR',
  '/reports':    'Reported Purchases',
  '/sync-health': 'Sync Health',
  '/coupons':   'Coupons',
  '/referrals': 'Referrals',
  '/loyalty':   'Loyalty',
  '/crm':            'CRM Segments',
  '/promo-codes':    'Promo Codes',
  '/notifications':  'Push Notifications',
  '/campaigns':      'WhatsApp Campaigns',
  '/pages':     'App Pages',
  '/settings':  'Settings',
};

function getAdminInfo() {
  try { return JSON.parse(localStorage.getItem('admin_info') || '{}'); } catch { return {}; }
}

function AdminAvatar({ name }) {
  const initials = name ? name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() : 'A';
  return <div className={styles.adminAvatar}>{initials}</div>;
}

export default function Layout() {
  // Polled rather than pushed: a report is not urgent to the second, and this
  // is one indexed count.
  const [openReports, setOpenReports] = useState(0);
  useEffect(() => {
    let stop = false;
    const tick = () => adminAPI.getDisputeCount()
      .then((r) => { if (!stop) setOpenReports(r.count || 0); })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 60_000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const admin    = getAdminInfo();

  // Sidebar state: pinned = always expanded, unpinned = collapsed, hover to expand
  const [pinned, setPinned] = useState(() => {
    const saved = localStorage.getItem('sidebar_pinned');
    return saved !== null ? saved === 'true' : true;
  });
  const [hovered, setHovered] = useState(false);

  const expanded = pinned || hovered;

  useEffect(() => {
    localStorage.setItem('sidebar_pinned', String(pinned));
  }, [pinned]);

  const pageTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    location.pathname.startsWith(path)
  )?.[1] || 'Admin';

  // A store login sees the counter and nothing else. The server refuses the
  // rest anyway, but a sidebar full of links that all say "not available to
  // store accounts" is its own kind of broken.
  // Drawn from what this account may actually reach, not from its role name.
  //
  // A page somebody cannot open is absent rather than greyed out, and that is
  // deliberate for the security pages: a disabled "Scan Alerts" tells whoever
  // sees it that there is something there worth working around. The server
  // answers 404 for the same reason.
  //
  // This only draws the menu. Every route decides for itself, because hiding
  // a link protects nothing.
  // Refreshed from the server rather than trusted from storage.
  //
  // A login saved before permissions existed has no list at all, and a menu
  // filtered on a missing list is an empty menu -- somebody would be locked
  // out of their own dashboard by an upgrade, with nothing on screen to
  // explain it. Asking the server also means suspending an account empties
  // the menu on the next page load rather than when the session expires.
  const [me, setMe] = useState(null);
  useEffect(() => {
    let gone = false;
    adminAPI.me()
      .then(r => { if (!gone) { setMe(r); localStorage.setItem('admin_info', JSON.stringify({ ...admin, ...r, fullName: r.name || admin.fullName })); } })
      .catch(() => {});
    return () => { gone = true; };
  }, []);

  // Unlooked-at scan alerts.
  //
  // A message to a phone is easy to miss and impossible to act on from a
  // phone, so the dashboard says so too -- but only to somebody who is
  // allowed to know. Anybody else gets a 404 from the count endpoint and the
  // bar never appears, which is the same rule as the page itself: a banner
  // saying "3 alerts" to a store manager tells them exactly what they need
  // to know about being watched.
  const [alerts, setAlerts] = useState(null);
  useEffect(() => {
    let gone = false;
    const tick = () => adminAPI.scanAlertCount()
      .then(r => { if (!gone) setAlerts(r); })
      .catch(() => { if (!gone) setAlerts(null); });
    tick();
    // Every two minutes. These are read and acted on by a person, not a
    // trading screen, and a tighter poll would be a request per user per
    // few seconds for a number that rarely changes.
    const id = setInterval(tick, 120000);
    return () => { gone = true; clearInterval(id); };
  }, []);

  // Until the answer arrives, fall back to what was stored. An account saved
  // before this existed has no list, and showing everything while we ask is
  // safe: the server refuses anything that is not theirs regardless.
  const live = me || admin;
  const isOwner = live?.role === 'superadmin' || live?.isSuperadmin === true;
  const allowed = Array.isArray(live?.permissions) ? live.permissions : null;
  const navItems = NAV_ITEMS.filter(item => {
    if (!item.perm) return true;
    if (isOwner) return true;
    if (allowed === null) return true;  // not told yet; the API still decides
    return allowed.includes(item.perm);
  });

  // One destination is not a menu. On a 1024px POS screen the sidebar was
  // spending a sixth of the width to show a single link to the page already
  // open, so it goes entirely and its two useful parts — who is signed in,
  // and signing out — move into the top bar.
  const soloPage = navItems.length <= 1;

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
    navigate('/login');
  };

  return (
    <div className={styles.shell}>
      {/* A scan worth looking at.
          Across the top rather than a dot on a bell: the point of these is
          that somebody is being quietly overcharged, and a notification that
          waits to be noticed is one that is not. Only ever drawn for an
          account allowed to see them -- anybody else gets a 404 from the
          count and this never renders. */}
      {alerts && alerts.total > 0 && (
        <div
          onClick={() => navigate('/scan-alerts')}
          style={{
            position:'fixed', top:0, left:0, right:0, zIndex:900,
            background: alerts.high > 0 ? '#B91C1C' : '#B45309',
            color:'#fff', padding:'9px 18px', cursor:'pointer',
            display:'flex', alignItems:'center', gap:10, fontSize:14,
            boxShadow:'0 2px 8px rgba(0,0,0,0.18)',
          }}
          title="Open the scan alerts"
        >
          <BellAlertIcon style={{ width:18, height:18, flexShrink:0 }} />
          <strong style={{ flexShrink:0 }}>
            {alerts.high > 0
              ? `${alerts.high} scan${alerts.high === 1 ? '' : 's'} worth checking`
              : `${alerts.total} to look at`}
          </strong>
          {alerts.headline && (
            <span style={{ opacity:0.9, overflow:'hidden', textOverflow:'ellipsis',
                           whiteSpace:'nowrap' }}>
              — {alerts.headline}
            </span>
          )}
          <span style={{ marginLeft:'auto', opacity:0.85, flexShrink:0 }}>Open &rsaquo;</span>
        </div>
      )}
      {alerts && alerts.total > 0 && <div style={{ height:38 }} />}

      {/* ── Sidebar ── */}
      {!soloPage && <aside
        className={`${styles.sidebar} ${expanded ? styles.sidebarExpanded : styles.sidebarCollapsed}`}
        onMouseEnter={() => !pinned && setHovered(true)}
        onMouseLeave={() => !pinned && setHovered(false)}
      >
        {/* Logo area */}
        <div className={styles.logo}>
          <img src={iconImg} alt="TTS" className={styles.logoIcon} />
          <div className={styles.logoText}>
            <div className={styles.logoTitle}>The Toy Store</div>
            <div className={styles.logoSub}>VIP Admin</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className={styles.nav}>
          <div className={`${styles.navSection} ${expanded ? styles.show : styles.hide}`}>MENU</div>
          {navItems.map(({ path, label, Icon }) => (
            <NavLink
              key={path}
              to={path}
              title={!expanded ? label : undefined}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className={styles.activeBar} />}
                  <span className={styles.navIconBox}>
                    <Icon style={{ width: 20, height: 20 }} />
                  </span>
                  <span className={`${styles.navLabel} ${expanded ? styles.show : styles.hide}`}>{label}</span>
                  {/* Unresolved reports. Shown collapsed as a dot, since a
                      number has nowhere to sit on a 20px icon. */}
                  {path === '/reports' && openReports > 0 && (
                    <span className={expanded ? styles.navBadge : styles.navDot}>
                      {expanded ? openReports : ''}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Pin toggle */}
        <button
          className={`${styles.pinBtn} ${expanded ? styles.show : styles.hide}`}
          onClick={() => setPinned(p => !p)}
          title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
        >
          {pinned
            ? <PinSolid style={{ width: 16, height: 16 }} />
            : <PinOutline style={{ width: 16, height: 16 }} />
          }
          <span>{pinned ? 'Unpin sidebar' : 'Pin sidebar'}</span>
        </button>

        {/* Admin footer */}
        <div className={styles.adminInfo}>
          <AdminAvatar name={admin.fullName} />
          <div className={`${styles.adminMeta} ${expanded ? styles.show : styles.hide}`}>
            <div className={styles.adminName}>{admin.fullName || 'Admin'}</div>
            <div className={styles.adminRole}>{admin.role || 'administrator'}</div>
          </div>
          <button
            className={`${styles.logoutIconBtn} ${expanded ? styles.show : styles.hide}`}
            onClick={handleLogout}
            title="Sign Out"
          >
            <ArrowRightOnRectangleIcon style={{ width: 17, height: 17 }} />
          </button>
        </div>
      </aside>}

      {/* Spacer when sidebar is collapsed (floating mode) */}
      {!soloPage && !pinned && <div className={styles.sidebarSpacer} />}

      {/* ── Right pane ── */}
      <div className={styles.rightPane}>
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <img src={logoImg} alt="The Toy Store" className={styles.topBarLogo} />
            <span className={styles.topBarDivider} />
            <span className={styles.topBarPage}>{pageTitle}</span>
          </div>
          <div className={styles.topBarRight}>
            <span className={styles.topBarDate}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            {/* With no sidebar these have nowhere else to live, and a shared
                counter terminal is exactly where knowing who is signed in —
                and being able to sign out — matters. */}
            {soloPage && (
              <>
                <span className={styles.topBarDivider} />
                {/* Avatar only. The branch name was redundant beside a page
                    already titled Counter, and it ate the width that the date
                    needs on a 1024px screen. Hovering still names it. */}
                <span title={admin.fullName || 'Counter'}>
                  <AdminAvatar name={admin.fullName} />
                </span>
                <button
                  className={styles.logoutIconBtn}
                  onClick={handleLogout}
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon style={{ width:16, height:16 }} />
                  <span className={styles.logoutLabel}>Sign out</span>
                </button>
              </>
            )}
          </div>
        </header>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
